import { Fragment, useMemo, useRef, useState } from 'react';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  DeleteOutlineRounded,
  UploadFileRounded,
} from '@mui/icons-material';

import IndustrialCostProductionView from './IndustrialCostProductionView';
import IndustrialCostDirectInputsView from './IndustrialCostDirectInputsView';
import IndustrialCostPurchaseOrdersSourceDialog from './IndustrialCostPurchaseOrdersSourceDialog';
import IndustrialCostManufacturingOrdersSourceDialog from './IndustrialCostManufacturingOrdersSourceDialog';
import IndustrialCostImportCenterView from './IndustrialCostImportCenterView';
import IndustrialCostBoxMasterSourceDialog from './IndustrialCostBoxMasterSourceDialog';
import IndustrialCostFileReviewPanel, {
  PLANT_DATA_REVIEW_QUERY_KEY,
} from './IndustrialCostFileReviewPanel';
import IndustrialCostStructuralUploadWizard from './IndustrialCostStructuralUploadWizard';
import { PlantProductionYearBlock } from './IndustrialCostPlantProductionYear';
import IndustrialCostInventoryDifferenceBlock from './IndustrialCostInventoryDifferenceBlock';
import type { StructuralFileKind } from './IndustrialCostStructuralUploadWizard';
import {
  DailyCoherenceLine,
  MasterTablesBlock,
  RECOUNT_STATUS_QUERY_KEY,
  RecountStartBlock,
  SectionHeading,
} from './IndustrialCostDataSourcesBlocks';
import type { MasterTableKey } from './IndustrialCostDataSourcesBlocks';
import {
  IMPORT_PERMISSION_DENIED_TEXT,
  useCanManageImports,
} from '../utils/importPermission';

import {
  getIndustrialCostDataSourceStatus,
} from '../services/industrialCostService';
import type {
  IndustrialCostDataSourceStatusItem,
} from '../services/industrialCostService';

import { esNumberFormat } from '../utils/numberFormat';
type DataSourceKey =
  | 'production'
  | 'direct-inputs'
  | 'manufacturing-orders'
  | 'purchase-orders'
  | 'inventory-count'
  | 'technical-consumptions'
  | 'box-customer-personalizations'
  | 'box-purchase-mappings';

// IC-STOCK-05: fuente de Gestión de datos -> maestro de cajas del backend.
const BOX_MASTER_KIND_BY_SOURCE = {
  'box-customer-personalizations': 'customer-personalizations',
  'box-purchase-mappings': 'box-purchase-mappings',
} as const;

type BoxMasterSourceKey = keyof typeof BOX_MASTER_KIND_BY_SOURCE;

function isBoxMasterSource(key: DataSourceKey): key is BoxMasterSourceKey {
  return key in BOX_MASTER_KIND_BY_SOURCE;
}

type DataSourceDefinition = {
  key: DataSourceKey;
  title: string;
  description: string;
  expectedFileName: string | null;
  frequency: string;
};

const DATA_SOURCES: DataSourceDefinition[] = [
  {
    key: 'production',
    title: 'Producción real e histórica',
    description:
      'Producción fabricada actual e histórica. Admite detalle diario y agregados mensuales o anuales para comparativas y KPIs.',
    expectedFileName: 'lparpolautomatico.xlsx',
    frequency: 'Actualización diaria + cargas históricas',
  },
  {
    key: 'direct-inputs',
    title: 'Entradas / albaranes',
    description:
      'Material recibido físicamente. Incrementa el stock desde el último recuento.',
    expectedFileName: 'lalbcpautomatico.xlsx',
    frequency: 'Actualización diaria',
  },
  {
    key: 'manufacturing-orders',
    title: 'OF / planificación pendiente',
    description:
      'Órdenes pendientes de fabricar, cantidades pendientes, máquinas y fechas de expedición.',
    expectedFileName: 'lplanifpol2automatico.xlsx',
    frequency: 'Snapshot actualizado',
  },
  {
    key: 'purchase-orders',
    title: 'Pedidos de compra pendientes',
    description:
      'Compras todavía no recibidas, cantidades pendientes y fechas previstas de entrega.',
    expectedFileName: 'lpedcpautomatico.xlsx',
    frequency: 'Snapshot actualizado',
  },
  {
    key: 'inventory-count',
    title: 'Recuento físico',
    description:
      'Inventario físico de materias primas utilizado como punto de partida del stock calculado.',
    expectedFileName: 'RECUENTO POL 2026.xlsx',
    frequency: 'Normalmente mensual',
  },
  {
    key: 'technical-consumptions',
    title: 'Escandallo',
    description:
      'Relación producto → materia prima y cantidad consumida. Será la base del cálculo de necesidades.',
    expectedFileName: null,
    frequency: 'Cuando cambie el escandallo',
  },
  {
    key: 'box-customer-personalizations',
    title: 'Cajas · personalización por cliente',
    description:
      'Qué personalización de caja usa cada cliente. Cada subida sustituye la relación completa.',
    expectedFileName: 'mapeo_clientes_personalizacion.xlsx',
    frequency: 'Cuando cambie',
  },
  {
    key: 'box-purchase-mappings',
    title: 'Cajas · caja real por personalización',
    description:
      'Caja genérica del escandallo + personalización → caja real comprada. Cada subida sustituye la relación completa.',
    expectedFileName: 'mapeo_cajas_reales.xlsx',
    frequency: 'Cuando cambie',
  },
];

function normalizedFileName(value: string) {
  return value.trim().toLowerCase();
}

function formatSourceStatusDate(
  value: string | null | undefined,
) {
  if (!value) return '—';

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
}

function sourceStatusLabel(
  item: IndustrialCostDataSourceStatusItem | undefined,
) {
  if (!item || item.status === 'not_found') {
    return 'Sin datos verificados';
  }

  if (item.status === 'published') {
    return 'Publicado';
  }

  return 'Datos disponibles';
}

type IndustrialCostDataSourcesViewProps = {
  onNavigate: (
    view:
      | 'inventory-register'
      | 'technical-consumptions'
      | 'direct-materials'
      | 'products'
  ) => void;
};

export default function IndustrialCostDataSourcesView({
  onNavigate,
}: IndustrialCostDataSourcesViewProps) {
  const queryClient = useQueryClient();

  const sourceStatusQuery = useQuery({
    queryKey: ['industrial-cost-data-source-status'],
    queryFn: getIndustrialCostDataSourceStatus,
  });

  const sourceStatusByKey = useMemo(
    () =>
      Object.fromEntries(
        (sourceStatusQuery.data ?? []).map((item) => [
          item.source_key,
          item,
        ]),
      ) as Partial<
        Record<DataSourceKey, IndustrialCostDataSourceStatusItem>
      >,
    [sourceStatusQuery.data],
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [pendingSourceKey, setPendingSourceKey] =
    useState<DataSourceKey | null>(null);

  const [selectedFiles, setSelectedFiles] = useState<
    Partial<Record<DataSourceKey, File>>
  >({});
  const [showIndividualDaily, setShowIndividualDaily] = useState(false);

  const [productionDialogOpen, setProductionDialogOpen] =
    useState(false);

  const [productionImportedMessage, setProductionImportedMessage] =
    useState<string | null>(null);

  const [directInputsDialogOpen, setDirectInputsDialogOpen] =
    useState(false);

  const [directInputsImportedMessage, setDirectInputsImportedMessage] =
    useState<string | null>(null);

  const [
    manufacturingOrdersDialogOpen,
    setManufacturingOrdersDialogOpen,
  ] = useState(false);

  const [
    manufacturingOrdersImportedMessage,
    setManufacturingOrdersImportedMessage,
  ] = useState<string | null>(null);


  const [purchaseDialogOpen, setPurchaseDialogOpen] =
    useState(false);

  const [purchaseImportedMessage, setPurchaseImportedMessage] =
    useState<string | null>(null);

  const [boxMasterDialogSource, setBoxMasterDialogSource] =
    useState<BoxMasterSourceKey | null>(null);

  // IC-STOCK-10: carga única de recuento, escandallo y Excel de cajas.
  const [structuralWizardOpen, setStructuralWizardOpen] = useState(false);
  // IND-011: el asistente se abre para el recuento o para las tablas maestras.
  const [structuralWizardKinds, setStructuralWizardKinds] = useState<
    Exclude<StructuralFileKind, 'ignore'>[]
  >(['box-customer', 'box-purchase', 'escandallo']);
  const [structuralWizardTitle, setStructuralWizardTitle] = useState('Cargar tablas maestras');

  const openStructuralWizard = (
    kinds: Exclude<StructuralFileKind, 'ignore'>[],
    title: string,
  ) => {
    setStructuralWizardKinds(kinds);
    setStructuralWizardTitle(title);
    setStructuralWizardOpen(true);
  };

  const [boxMasterImportedMessages, setBoxMasterImportedMessages] =
    useState<Partial<Record<BoxMasterSourceKey, string>>>({});

  // IND-007
  const canImport = useCanManageImports();

  const handleSelectFile = (sourceKey: DataSourceKey) => {
    if (!canImport) return;
    setPendingSourceKey(sourceKey);
    fileInputRef.current?.click();
  };



  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file || !pendingSourceKey) {
      event.target.value = '';
      return;
    }

    const sourceKey = pendingSourceKey;

    event.target.value = '';
    setPendingSourceKey(null);

    if (sourceKey === 'manufacturing-orders') {
      setManufacturingOrdersImportedMessage(null);

      setSelectedFiles((current) => ({
        ...current,
        'manufacturing-orders': file,
      }));

      setManufacturingOrdersDialogOpen(true);
      return;
    }



    if (sourceKey === 'purchase-orders') {
      setPurchaseImportedMessage(null);

      setSelectedFiles((current) => ({
        ...current,
        'purchase-orders': file,
      }));

      setPurchaseDialogOpen(true);
      return;
    }

    if (isBoxMasterSource(sourceKey)) {
      setBoxMasterImportedMessages((current) => ({
        ...current,
        [sourceKey]: undefined,
      }));

      setSelectedFiles((current) => ({
        ...current,
        [sourceKey]: file,
      }));

      setBoxMasterDialogSource(sourceKey);
      return;
    }

    setSelectedFiles((current) => ({
      ...current,
      [sourceKey]: file,
    }));

    if (sourceKey === 'production') {
      setProductionImportedMessage(null);
    }

    if (sourceKey === 'direct-inputs') {
      setDirectInputsImportedMessage(null);
    }
  };

  const handleRemoveFile = (sourceKey: DataSourceKey) => {
    setSelectedFiles((current) => {
      const next = { ...current };
      delete next[sourceKey];
      return next;
    });

    if (sourceKey === 'production') {
      setProductionDialogOpen(false);
      setProductionImportedMessage(null);
    }

    if (sourceKey === 'direct-inputs') {
      setDirectInputsDialogOpen(false);
      setDirectInputsImportedMessage(null);
    }

    if (sourceKey === 'manufacturing-orders') {
      setManufacturingOrdersDialogOpen(false);
      setManufacturingOrdersImportedMessage(null);
    }

    if (sourceKey === 'purchase-orders') {
      setPurchaseDialogOpen(false);
      setPurchaseImportedMessage(null);
    }

    if (isBoxMasterSource(sourceKey)) {
      setBoxMasterDialogSource(null);
      setBoxMasterImportedMessages((current) => ({
        ...current,
        [sourceKey]: undefined,
      }));
    }
  };

  return (
    <Stack spacing={3}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={handleFileChange}
      />

      <Box>
        <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 900 }}>
          INDUSTRIAL COST · GESTIÓN DE DATOS
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 950 }}>
            Gestión de datos
          </Typography>
          <Button
            variant="text"
            onClick={() =>
              document
                .getElementById('revisa-tus-ficheros')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Revisa tus ficheros
          </Button>
        </Stack>
      </Box>

      {/* IND-011 · 1. Punto de partida */}
      <RecountStartBlock
        canImport={canImport}
        onLoad={() => openStructuralWizard(['recuento'], 'Cargar nuevo recuento')}
      />

      {/* IND-023: diferencia de inventario, junto al recuento */}
      <IndustrialCostInventoryDifferenceBlock />

      {/* IND-011 · 2. Cada día */}
      <SectionHeading
        step="2 · CADA DÍA"
        title="Carga diaria de los 4 ficheros del ERP"
        subtitle="Producción, albaranes, OF pendientes y compras pendientes de la carpeta del día."
      />

      <IndustrialCostImportCenterView
        compact
        sourceStatuses={sourceStatusQuery.data}
        sourceStatusesLoading={sourceStatusQuery.isLoading}
        sourceStatusesError={sourceStatusQuery.isError}
        onImported={() => {
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-data-source-status'] }),
            queryClient.invalidateQueries({ queryKey: PLANT_DATA_REVIEW_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-plant-data-freshness'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-plant-dashboard'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-material-stock-projection'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-planning-context'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-planning-week-context'] }),
            // IND-022
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-by-month'] }),
          ]);
        }}
      />

      <DailyCoherenceLine />

      {/* IND-022: producción del año por mes y festivos */}
      <PlantProductionYearBlock />

      {/* IND-011 · 3. Solo cuando cambien */}
      <MasterTablesBlock
        canImport={canImport}
        statusByKey={sourceStatusByKey}
        onLoad={(key: MasterTableKey) => {
          if (key === 'technical-consumptions') {
            openStructuralWizard(['escandallo'], 'Cargar escandallo');
            return;
          }
          handleSelectFile(key);
        }}
        onLoadSeveral={() =>
          openStructuralWizard(
            ['box-customer', 'box-purchase', 'escandallo'],
            'Cargar tablas maestras',
          )
        }
      />

      <IndustrialCostStructuralUploadWizard
        open={structuralWizardOpen}
        kinds={structuralWizardKinds}
        title={structuralWizardTitle}
        onClose={() => setStructuralWizardOpen(false)}
        onFinished={() => {
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: PLANT_DATA_REVIEW_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: RECOUNT_STATUS_QUERY_KEY }),
            // IND-023: la carga del recuento guarda su comparación.
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-inventory-differences'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-plant-data-freshness'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-data-source-status'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-material-stock-projection'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-plant-dashboard'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-technical-consumption-rules'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-technical-consumption-import-pending'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-direct-materials'] }),
            queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-planning-context'] }),
          ]);
        }}
      />

      {/* IND-011 · 4. Revisa tus ficheros */}
      <IndustrialCostFileReviewPanel />

      {/* IND-011 · Avanzado: cargas sueltas, producción histórica y maestros */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ color: '#e2e8f0', fontWeight: 900, fontSize: 16 }}>
            Avanzado
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
            Cargas sueltas de cada fichero, producción histórica y maestros de productos y
            materiales. Cada carga pide confirmación antes de sustituir datos.
          </Typography>
        </Box>
        <Button variant="outlined" sx={{ textTransform: 'none' }}
          onClick={() => setShowIndividualDaily((prev) => !prev)}>
          {showIndividualDaily ? 'Ocultar avanzado' : 'Mostrar avanzado'}
        </Button>
      </Stack>

      {showIndividualDaily ? (
      <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {DATA_SOURCES.map((source) => {
          const selectedFile = selectedFiles[source.key];
          const realStatus =
            sourceStatusByKey[source.key];

          const sectionHeader =
            source.key === 'production'
              ? {
                  title: 'ACTUALIZACIÓN DIARIA',
                  description:
                    'Producción, entradas, OF pendientes y compras. Son las fuentes operativas que deben poder renovarse en pocos minutos.',
                }
              : source.key === 'inventory-count'
                ? {
                    title: 'RECUENTO FÍSICO',
                    description:
                      'Corte mensual de stock. Materias primas y embalajes comparten el mismo origen físico del recuento.',
                  }
                : source.key === 'technical-consumptions'
                  ? {
                      title: 'DATOS ESTRUCTURALES',
                      description:
                        'Escandallo y maestros que explican cómo un producto se transforma en necesidades de materiales.',
                    }
                  : null;

          const matchesExpectedFile =
            source.key === 'production' ||
            !selectedFile ||
            !source.expectedFileName ||
            normalizedFileName(selectedFile.name) ===
              normalizedFileName(source.expectedFileName);

          return (
            <Fragment key={source.key}>
              {sectionHeader ? (
                <Box
                  sx={{
                    gridColumn: '1 / -1',
                    pt:
                      source.key === 'production'
                        ? 0
                        : 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#7dd3fc',
                      fontSize: 12,
                      fontWeight: 950,
                      letterSpacing: 0.8,
                    }}
                  >
                    {sectionHeader.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: 12,
                      mt: 0.35,
                      maxWidth: 900,
                    }}
                  >
                    {sectionHeader.description}
                  </Typography>
                </Box>
              ) : null}

              <Paper
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: 'rgba(15,23,42,0.72)',
                border: '1px solid rgba(148,163,184,0.16)',
              }}
            >
              <Stack spacing={2} sx={{ p: 2.5 }}>
                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: 'flex-start',
                    sm: 'center',
                  }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontSize: '1.05rem',
                        fontWeight: 950,
                      }}
                    >
                      {source.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b' }}
                    >
                      {source.frequency}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      sourceStatusQuery.isLoading
                        ? 'Comprobando...'
                        : sourceStatusLabel(realStatus)
                    }
                    color={
                      realStatus?.status === 'published'
                        ? 'success'
                        : realStatus?.status === 'data_available'
                          ? 'info'
                          : 'default'
                    }
                    sx={{ fontWeight: 800 }}
                  />
                </Stack>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 13,
                    lineHeight: 1.6,
                    minHeight: 42,
                  }}
                >
                  {source.description}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.4,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(2,6,23,0.28)',
                    border:
                      '1px solid rgba(148,163,184,0.10)',
                  }}
                >
                  <Stack spacing={0.45}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 850,
                      }}
                    >
                      Estado real
                    </Typography>

                    {sourceStatusQuery.isLoading ? (
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: 12,
                        }}
                      >
                        Comprobando datos publicados...
                      </Typography>
                    ) : realStatus &&
                      realStatus.status !== 'not_found' ? (
                      <>
                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontSize: 12.5,
                            fontWeight: 800,
                          }}
                        >
                          {sourceStatusLabel(realStatus)}
                          {' · '}
                          {formatSourceStatusDate(
                            realStatus.effective_at,
                          )}
                        </Typography>

                        {realStatus.data_date ? (
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8' }}
                          >
                            Fecha de dato: {realStatus.data_date}
                          </Typography>
                        ) : null}

                        {realStatus.source_file_name ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#94a3b8',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            Archivo: {realStatus.source_file_name}
                          </Typography>
                        ) : null}

                        {realStatus.rows_count !== null ? (
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8' }}
                          >
                            Filas registradas:{' '}
                            {esNumberFormat().format(
                              realStatus.rows_count,
                            )}
                          </Typography>
                        ) : null}
                      </>
                    ) : (
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: 12,
                        }}
                      >
                        No se ha encontrado evidencia verificable
                        para esta fuente.
                      </Typography>
                    )}
                  </Stack>
                </Paper>

                <Divider
                  sx={{
                    borderColor: 'rgba(148,163,184,0.12)',
                  }}
                />

                <Stack spacing={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 800,
                    }}
                  >
                    {source.key === 'production'
                      ? 'Archivo de referencia para la carga diaria'
                      : 'Archivo esperado inicialmente'}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#cbd5e1',
                      fontFamily: 'monospace',
                      fontSize: 13,
                    }}
                  >
                    {source.expectedFileName ??
                      'Pendiente de definir'}
                  </Typography>
                </Stack>

                {selectedFile ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      bgcolor: 'rgba(2,6,23,0.34)',
                      border:
                        '1px solid rgba(148,163,184,0.12)',
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          fontWeight: 800,
                        }}
                      >
                        Archivo seleccionado
                      </Typography>

                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {selectedFile.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b' }}
                      >
                        {esNumberFormat().format(selectedFile.size)}{' '}
                        bytes
                      </Typography>
                    </Stack>
                  </Paper>
                ) : null}

                {!matchesExpectedFile ? (
                  <Alert severity="warning" variant="outlined">
                    El archivo seleccionado no coincide con el nombre
                    esperado para esta fuente.
                  </Alert>
                ) : null}



                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {source.key === 'inventory-count' ? (
                    <Button
                      variant="contained"
                      onClick={() =>
                        onNavigate('inventory-register')
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      Abrir recuento
                    </Button>
                  ) : source.key === 'technical-consumptions' ? (
                    <Button
                      variant="contained"
                      onClick={() =>
                        onNavigate('technical-consumptions')
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      Abrir escandallo
                    </Button>
                  ) : (
                    <>
                    {!canImport ? (
                      <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                        {IMPORT_PERMISSION_DENIED_TEXT}
                      </Typography>
                    ) : null}
                    <Button
                      variant="contained"
                      startIcon={<UploadFileRounded />}
                      onClick={() =>
                        handleSelectFile(source.key)
                      }
                      disabled={!canImport}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      {selectedFile
                        ? 'Cambiar archivo'
                        : 'Seleccionar archivo'}
                    </Button>
                    </>
                  )}



                  {selectedFile ? (
                    <Button
                      variant="outlined"
                      startIcon={
                        <DeleteOutlineRounded />
                      }
                      onClick={() =>
                        handleRemoveFile(source.key)
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 850,
                      }}
                    >
                      Quitar
                    </Button>
                  ) : null}

                  {[
                    'production',
                    'direct-inputs',
                    'manufacturing-orders',
                    'purchase-orders',
                    'box-customer-personalizations',
                    'box-purchase-mappings',
                  ].includes(source.key) ? (
                    <Button
                      variant="outlined"
                      disabled={!selectedFile || !canImport}
                      onClick={() => {
                        // IND-011: confirmación antes de sustituir datos.
                        if (
                          !window.confirm(
                            'Esta carga suelta sustituye los datos de esta fuente. ¿Continuar?',
                          )
                        ) {
                          return;
                        }
                        if (source.key === 'production') {
                          setProductionDialogOpen(true);
                        }

                        if (source.key === 'direct-inputs') {
                          setDirectInputsDialogOpen(true);
                        }

                        if (source.key === 'manufacturing-orders') {
                          setManufacturingOrdersDialogOpen(true);
                        }

                        if (source.key === 'purchase-orders') {
                          setPurchaseDialogOpen(true);
                        }

                        if (isBoxMasterSource(source.key)) {
                          setBoxMasterDialogSource(source.key);
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 850,
                      }}
                    >
                      {(source.key === 'production' &&
                        productionImportedMessage) ||
                      (source.key === 'direct-inputs' &&
                        directInputsImportedMessage) ||
                      (source.key === 'manufacturing-orders' &&
                        manufacturingOrdersImportedMessage) ||
                      (source.key === 'purchase-orders' &&
                        purchaseImportedMessage) ||
                      (isBoxMasterSource(source.key) &&
                        boxMasterImportedMessages[source.key])
                        ? 'Revisar configuración'
                        : 'Configurar'}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              </Paper>
            </Fragment>
          );
        })}
      </Box>

      <Paper
        sx={{
          p: 2.5,
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: 16,
              }}
            >
              Maestros relacionados
            </Typography>

            <Typography
              sx={{
                color: '#64748b',
                fontSize: 12,
                mt: 0.4,
                maxWidth: 720,
              }}
            >
              Productos y materiales son las referencias contra las que se
              validan producción, escandallo, inventario y compras.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              onClick={() => onNavigate('products')}
              sx={{
                textTransform: 'none',
                fontWeight: 850,
              }}
            >
              Productos
            </Button>

            <Button
              variant="outlined"
              onClick={() => onNavigate('direct-materials')}
              sx={{
                textTransform: 'none',
                fontWeight: 850,
              }}
            >
              Materiales directos
            </Button>
          </Stack>
        </Stack>
      </Paper>
      </>
      ) : null}

      {productionImportedMessage ? (
        <Alert severity="success" variant="outlined">
          Producción real: {productionImportedMessage}
        </Alert>
      ) : null}

      {manufacturingOrdersImportedMessage ? (
        <Alert severity="success" variant="outlined">
          OF pendientes: {manufacturingOrdersImportedMessage}
        </Alert>
      ) : null}

      {purchaseImportedMessage ? (
        <Alert severity="success" variant="outlined">
          Pedidos de compra: {purchaseImportedMessage}
        </Alert>
      ) : null}

      {boxMasterImportedMessages['box-customer-personalizations'] ? (
        <Alert severity="success" variant="outlined">
          Cajas · personalización por cliente:{' '}
          {boxMasterImportedMessages['box-customer-personalizations']}
        </Alert>
      ) : null}

      {boxMasterImportedMessages['box-purchase-mappings'] ? (
        <Alert severity="success" variant="outlined">
          Cajas · caja real por personalización:{' '}
          {boxMasterImportedMessages['box-purchase-mappings']}
        </Alert>
      ) : null}

      <Alert severity="warning" variant="outlined">
        Los archivos seleccionados solo viven en la sesión actual del
        navegador. Al refrescar se perderá la selección. Las fuentes con
        importador conectado solo escriben datos después de una confirmación
        explícita del usuario.
      </Alert>

      {productionDialogOpen && selectedFiles.production ? (
        <IndustrialCostProductionView
          importOnly
          initialImportFile={selectedFiles.production}
          onImportClose={() => {
            setProductionDialogOpen(false);
          }}
          onImportSuccess={(message) => {
            setProductionImportedMessage(message);
            setProductionDialogOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ['industrial-cost-data-source-status'],
            });
            void queryClient.invalidateQueries({
              queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
            });
          }}
        />
      ) : null}

      {directInputsDialogOpen && selectedFiles['direct-inputs'] ? (
        <IndustrialCostDirectInputsView
          importOnly
          initialImportFile={selectedFiles['direct-inputs']}
          onImportClose={() => {
            setDirectInputsDialogOpen(false);
          }}
          onImportSuccess={(message) => {
            setDirectInputsImportedMessage(message);
            setDirectInputsDialogOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ['industrial-cost-data-source-status'],
            });
            void queryClient.invalidateQueries({
              queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
            });
          }}
        />
      ) : null}

      {manufacturingOrdersDialogOpen &&
      selectedFiles['manufacturing-orders'] ? (
        <IndustrialCostManufacturingOrdersSourceDialog
          open
          file={selectedFiles['manufacturing-orders']}
          onClose={() => {
            setManufacturingOrdersDialogOpen(false);
          }}
          onImported={(message) => {
            setManufacturingOrdersImportedMessage(message);
            setManufacturingOrdersDialogOpen(false);
            void Promise.all([
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-plant-dashboard'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-production-planning-context'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-production-planning-week-context'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-material-stock-projection'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-production-existing-orders-candidates'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-data-source-status'],
              }),
              queryClient.invalidateQueries({
                queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
              }),
            ]);
          }}
        />
      ) : null}

      {purchaseDialogOpen && selectedFiles['purchase-orders'] ? (
        <IndustrialCostPurchaseOrdersSourceDialog
          open
          file={selectedFiles['purchase-orders']}
          onClose={() => {
            setPurchaseDialogOpen(false);
          }}
          onImported={(message) => {
            setPurchaseImportedMessage(message);
            setPurchaseDialogOpen(false);
            void Promise.all([
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-material-stock-projection'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-plant-dashboard'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-data-source-status'],
              }),
              queryClient.invalidateQueries({
                queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
              }),
            ]);
          }}
        />
      ) : null}

      {boxMasterDialogSource && selectedFiles[boxMasterDialogSource] ? (
        <IndustrialCostBoxMasterSourceDialog
          open
          kind={BOX_MASTER_KIND_BY_SOURCE[boxMasterDialogSource]}
          file={selectedFiles[boxMasterDialogSource] as File}
          onClose={() => {
            setBoxMasterDialogSource(null);
          }}
          onImported={(message) => {
            const sourceKey = boxMasterDialogSource;
            setBoxMasterImportedMessages((current) => ({
              ...current,
              [sourceKey]: message,
            }));
            setBoxMasterDialogSource(null);
            void Promise.all([
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-material-stock-projection'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-plant-dashboard'],
              }),
              queryClient.invalidateQueries({
                queryKey: ['industrial-cost-data-source-status'],
              }),
              queryClient.invalidateQueries({
                queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
              }),
            ]);
          }}
        />
      ) : null}
    </Stack>
  );
}